// SPEC-27 · Conversa, the screen a sign-in opens. Lena points the athlete
// to her next assessment, or says everything is done. Nothing of it is
// kept: it is worked out again from the hub each time the tab shows.
//
// Which assessment, how much is left and in which unit, and the greeting
// by hour are poweryourflow-lib's (conversaProposal, greetingAt); this
// screen words them. Opening an assessment from here records Conversa as
// its origin, where « Sair » and its confirmation lead back.
//
// SPEC-28 (the founder's answer 1): after « Confirmar respostas » from
// here, Lena's thanks for that assessment come first, once.
//
// SPEC-31: every word follows the app's language, the date of the day and
// the thanks too: in PT they are the questionnaire's own closing words, in
// another language they name the assessment by its hub title.

import { useEffect, useState } from "react";
import { conversaProposal, greetingAt } from "poweryourflow-lib";
import type { ConversaProposal } from "poweryourflow-lib";

import { PyfBubble } from "../components/Chat";
import { NavPill } from "../components/NavPill";
import { todayLine } from "../i18n/language";
import type { Copy } from "../i18n/language";
import { useCopy, useLanguageFade } from "../i18n/LanguageProvider";
import { forgetFinished, justFinished, rememberOrigin } from "../lib/place";
import { assessmentPaths, paths, useRouter } from "../lib/router";
import type { AssessmentId } from "../schema/types";
import { useHub } from "./useHub";
import type { HubSource } from "./useHub";

const wideButton =
  "flex h-[var(--control-h)] w-full items-center justify-center gap-2 rounded-pill text-body font-semibold " +
  "transition-colors duration-[var(--dur-micro)] ease-pyf";

/** Lena's bubbles for this proposal, in the order she says them. */
function linesOf(text: Copy["conversa"], proposal: ConversaProposal, hello: string): readonly string[] {
  switch (proposal.kind) {
    case "welcome":
      return text.welcome(proposal.count, proposal.minutes);
    case "resume":
      return text.resume(hello, proposal.left, text.units[proposal.unit]);
    case "next":
      return text.next(hello, proposal.remaining, proposal.minutes);
    case "allDone":
      return text.allDone(hello);
  }
}

/** The card under the bubbles: the assessment proposed, or « Tudo em dia ». */
function ProposalCard({ proposal, onOpen }: { proposal: ConversaProposal; onOpen: (assessmentId: AssessmentId) => void }) {
  const words = useCopy();
  const text = words.conversa;
  if (proposal.kind === "allDone") {
    return (
      <div data-testid="proposal" className="flex items-end justify-between rounded-card bg-lime-tint p-5">
        <span className="flex flex-col gap-1">
          <span className="text-title font-semibold tracking-[var(--ls-title)] text-lime-ink">
            <span aria-hidden="true">✓ </span>
            {text.doneTitle}
          </span>
          <span className="text-body-sm text-lime-ink">{text.doneLine(proposal.done)}</span>
        </span>
        <span className="text-title font-medium text-dark">{text.doneCount(proposal.done, proposal.total)}</span>
      </div>
    );
  }
  const row = words.assessments.rows[proposal.assessmentId];
  const kicker = proposal.kind === "resume" ? text.progressKicker(row.kicker) : text.todoKicker(row.kicker);
  const meta =
    proposal.kind === "resume"
      ? text.progressMeta(proposal.answered, proposal.total)
      : text.todoMeta(proposal.count, words.assessments.units[proposal.unit], proposal.minutes);
  return (
    <button
      type="button"
      data-testid="proposal"
      onClick={() => onOpen(proposal.assessmentId)}
      className="flex items-center gap-4 rounded-card bg-violet-tint p-5 text-left"
    >
      <span className="flex min-w-0 flex-1 flex-col gap-1">
        <span className="text-caption font-semibold text-violet-ink">{kicker}</span>
        <span className="text-title font-semibold tracking-[var(--ls-title)] text-dark">{row.title}</span>
        <span className="text-body-sm text-violet-ink">{meta}</span>
      </span>
      <span className="flex size-[var(--touch-min)] shrink-0 items-center justify-center rounded-pill bg-violet text-title text-white">
        <span aria-hidden="true">→</span>
        <span className="sr-only">{text.open}</span>
      </span>
    </button>
  );
}

export function Conversa({ source }: { source: HubSource }) {
  const { navigate } = useRouter();
  const words = useCopy();
  const text = words.conversa;
  const fade = useLanguageFade();
  const { hub, failed, retry, runOf } = useHub(source, "conversa");
  // Read once per visit, forgotten after: the next visit opens on the proposal alone.
  const [finished] = useState(justFinished);
  useEffect(() => {
    if (finished) forgetFinished();
  }, [finished]);

  const now = new Date();
  const proposal = hub ? conversaProposal(runOf) : null;
  const firstName = hub?.profile?.firstName ?? "";
  const hello = text.hello(text.greetings[greetingAt(now.getHours())], firstName);
  const thanks = finished
    ? text.thanks(firstName, words.assessments.rows[finished.assessmentId].title, finished.assessmentId, finished.closing)
    : [];

  const open = (assessmentId: AssessmentId) => {
    rememberOrigin(assessmentId, "conversa");
    navigate(assessmentPaths[assessmentId]);
  };

  return (
    <main className="flex h-dvh flex-col bg-cream">
      {/* The pill scrolls with the conversation: it is not fixed (SPEC-27, Action list). */}
      <div className="flex flex-1 flex-col overflow-y-auto">
        <NavPill active="conversa" firstName={firstName} />
        <section
          aria-live="polite"
          aria-busy={!proposal && !failed}
          aria-label={proposal ? undefined : text.loading}
          className={`${fade || "tab-enter"} flex flex-1 flex-col justify-end gap-2 px-5 py-4`}
        >
          <p className="pb-4 text-center text-caption text-muted">{todayLine(words, now)}</p>

          {failed && (
            <div className="flex flex-col gap-3">
              <p className="text-body leading-[var(--lh-body)] text-muted">{text.failed}</p>
              <button type="button" onClick={retry} className={`${wideButton} bg-dark text-cream hover:bg-dark-hover`}>
                {text.retry}
              </button>
            </div>
          )}

          {/* Worked out again on every visit, so it arrives with the tab's 120 ms fade alone: no rise, no bubble entrance (review-animations, SPEC-27). */}
          {proposal && (
            <div className="tab-enter flex flex-col gap-2">
              {[...thanks, ...linesOf(text, proposal, hello)].map((line) => (
                <div key={line} className="flex items-end gap-3">
                  <span aria-hidden="true" className="mb-1 size-8 shrink-0 rounded-pill bg-violet" />
                  <PyfBubble enter={false}>{line}</PyfBubble>
                </div>
              ))}
              <div className="flex flex-col gap-3 pt-2 pl-11">
                <ProposalCard proposal={proposal} onOpen={open} />
                {proposal.kind === "allDone" ? (
                  <button
                    type="button"
                    onClick={() => navigate(paths.assessments)}
                    className={`${wideButton} border border-line-20 bg-white text-dark hover:bg-cream-deep`}
                  >
                    {text.toAssessments}
                    <span aria-hidden="true">→</span>
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => open(proposal.assessmentId)}
                    className={`${wideButton} bg-dark text-cream hover:bg-dark-hover`}
                  >
                    {proposal.kind === "resume" ? text.resumeButton : text.start}
                    <span aria-hidden="true">→</span>
                  </button>
                )}
              </div>
            </div>
          )}
        </section>
      </div>

      {/* Visible and out of scope (SPEC-27): nothing is sent in this version. */}
      <footer className="flex items-center gap-3 px-5 pt-2 pb-5">
        <input
          disabled
          placeholder={text.composer}
          aria-label={text.composer}
          className="h-[var(--composer-h)] min-w-0 flex-1 rounded-pill border border-line-10 bg-white px-5 text-body-lg placeholder:text-muted"
        />
        <button
          type="button"
          disabled
          className="flex size-[var(--composer-h)] shrink-0 items-center justify-center rounded-pill bg-violet text-title text-white"
        >
          <span aria-hidden="true">↑</span>
          <span className="sr-only">{text.send}</span>
        </button>
      </footer>
    </main>
  );
}
